import {
  ApiError,
  type AuditEventPage,
  type BeginAuthenticatorEnrollmentResponse,
  type LoginOutcome,
  type LoginResponse,
  type CompanyTimeZoneResponse,
  type MeResponse,
  type MembershipResponse,
  type MfaEnrollmentResponse,
  type PermissionResponse,
  type RoleResponse,
  type TrustedDeviceResponse,
} from '../types';
import type { IdentityGateway } from './IdentityGateway';
import { getMockMemberships, resetMockMemberships, seedMockMemberships } from './mockCompanyMembershipStore';
import { getMockDelegation } from './mockDelegationState';
import {
  getMockMfaEnrollments,
  getMockStepUp,
  getMockTrustedDevices,
  getPendingAuthenticatorSecret,
  setMockMfaEnrollments,
  setMockStepUp,
  setMockTrustedDevices,
  setPendingAuthenticatorSecret,
} from './mockSecuritySettingsStore';
import { clearMockCurrentUser, MOCK_ADMIN_USER_ID, MOCK_APPROVER_USER_ID, setMockCurrentUser } from './mockCurrentUser';

const MOCK_COMPANY_ID = '11111111-1111-1111-1111-111111111111';
// EPN-91: the normal mock identity (admin@epn.local) is a CompanyAdmin, the established company-scoped
// Admin role — not the platform-wide SuperAdmin. This id matches the CompanyAdmin entry listRoles() below
// already registers, so the role assignment and the role registry agree. SuperAdmin is preserved as a
// distinct, separately-registered role (see SUPERADMIN_ROLE_ID) for future Super Admin portal work — it is
// no longer assigned to any normal mock user.
const COMPANY_ADMIN_ROLE_ID = 'role-company-admin';
const SUPERADMIN_ROLE_ID = '22222222-2222-2222-2222-222222222222';
const HR_PAYROLL_ADMIN_ROLE_ID = 'role-hr-payroll-admin';
const MOCK_ROLES: RoleResponse[] = [
  { id: SUPERADMIN_ROLE_ID, name: 'SuperAdmin', isSystemRole: true },
  { id: COMPANY_ADMIN_ROLE_ID, name: 'CompanyAdmin', isSystemRole: false },
  { id: HR_PAYROLL_ADMIN_ROLE_ID, name: 'HRPayrollAdmin', isSystemRole: false },
];
const MOCK_USER_ID = MOCK_ADMIN_USER_ID;
const MOCK_MEMBERSHIP_ID = '44444444-4444-4444-4444-444444444444';

/** Hardcoded so the login-security screens (MFA challenge, lockout, forgot/reset password, unlock) are
 * all exercisable via `VITE_USE_MOCKS=true` without a running backend — see this class's own doc
 * comment. Mirrors admin@epn.local's shape but requires an Email MFA code of "123456" (logged to the
 * console instead of "sent", never rendered on screen — see this file's `mfaChallenge()`). */
// EPN-104: a second ordinary mock user — same company and CompanyAdmin role as admin@epn.local, but a different
// user id — so the Payroll Review two-user flow (User A prepares/submits, User B approves) is reachable in mock
// mode and the segregation-of-duties comparison is meaningful. Mock mode only; never part of real auth.
const APPROVER_USER_EMAIL = 'approver@epn.local';
/** Partner Portal fixture (mock mode only): a person who works for the synthetic partner firm and has NO company at all, like a real partner-only user. The partner firm itself lives in
 * MockPartnerGateway; the real server answers /api/partner/me from the database. Shares the mock password. */
const PARTNER_USER_EMAIL = 'partner@epn.local';
const PARTNER_USER_ID = '77777777-7777-7777-7777-777777777701';
const APPROVER_USER_ID = MOCK_APPROVER_USER_ID;
const APPROVER_MEMBERSHIP_ID = '88888888-8888-8888-8888-888888888802';

const MFA_USER_EMAIL = 'mfa@epn.local';
const MFA_USER_ID = '55555555-5555-5555-5555-555555555555';
const MFA_MEMBERSHIP_ID = '99999999-9999-9999-9999-999999999999';
const MFA_CHALLENGE_TOKEN = '66666666-6666-6666-6666-666666666666';
const MFA_TEST_CODE = '123456';
/** Mirrors the API's default step-up verification window (10 minutes). */
const MOCK_STEP_UP_WINDOW_MS = 10 * 60 * 1000;

/** Hardcoded so the Account Locked screen is reachable in mock mode without waiting out a real lockout
 * window. */
const LOCKED_USER_EMAIL = 'locked@epn.local';
const LOCKED_LOCKOUT_END_UTC = new Date(Date.now() + 15 * 60 * 1000).toISOString();

/** Hardcoded reset-password tokens so ResetPasswordPage's valid/expired branches are both reachable in
 * mock mode. */
const VALID_RESET_TOKEN = 'mock-valid-reset-token';
const EXPIRED_RESET_TOKEN = 'mock-expired-reset-token';

/** Hardcoded unlock-complete token, same reasoning as the reset tokens above — any other value (e.g.
 * "mock-expired-unlock-token") exercises the generic invalid/expired branch. */
const VALID_UNLOCK_TOKEN = 'mock-valid-unlock-token';

/** EPN-47's dedicated multi-company fixture — deliberately a SEPARATE mock identity from admin@epn.local
 * rather than adding a second membership to that one, so every existing test that logs in as
 * admin@epn.local keeps exercising the single-company auto-select path (see IdentityProvider.tsx) exactly
 * as before. Three memberships: the current company, a normal one, and one with a long name (matching the
 * supplied design package's own "Long Business Name" callout) — enough to exercise BusinessViewSwitcher's
 * default-list, search, and wrapping states end-to-end against a real (mocked) switch-company round trip.
 * This is this identity's INITIAL seed value only — like admin@epn.local and mfa@epn.local, its live
 * membership list lives in the shared `mockCompanyMembershipStore` (keyed by MULTI_USER_ID) and can grow
 * via `MockCompaniesGateway.create()`, exactly like the other two identities. `login()`'s early return for
 * this identity (bypassing the lockout/MFA checks) is still its own branch — see `loggedInAs` below — but
 * `getMe()`/`switchCompany()` use the exact same shared-store-driven logic for all three identities. */
const MULTI_USER_EMAIL = 'multi@epn.local';
const MULTI_USER_ID = '77777777-7777-7777-7777-777777777701';
const MULTI_ROLE_ID = '77777777-7777-7777-7777-777777777702';
const MULTI_COMPANY_A_ID = '77777777-7777-7777-7777-777777777711';
const MULTI_COMPANY_B_ID = '77777777-7777-7777-7777-777777777712';
const MULTI_COMPANY_C_ID = '77777777-7777-7777-7777-777777777713';
const MULTI_MEMBERSHIP_A = '77777777-7777-7777-7777-777777777721';
const MULTI_MEMBERSHIP_B = '77777777-7777-7777-7777-777777777722';
const MULTI_MEMBERSHIP_C = '77777777-7777-7777-7777-777777777723';
const MULTI_MEMBERSHIPS = [
  { membershipId: MULTI_MEMBERSHIP_A, companyId: MULTI_COMPANY_A_ID, companyName: 'Acme Corporation', roleId: MULTI_ROLE_ID, roleName: 'CompanyAdmin' },
  { membershipId: MULTI_MEMBERSHIP_B, companyId: MULTI_COMPANY_B_ID, companyName: 'Brightpath Marketing and Development Inc.', roleId: MULTI_ROLE_ID, roleName: 'CompanyAdmin' },
  { membershipId: MULTI_MEMBERSHIP_C, companyId: MULTI_COMPANY_C_ID, companyName: 'Bluepeak Solutions', roleId: MULTI_ROLE_ID, roleName: 'CompanyAdmin' },
];

/** admin@epn.local's sole membership before any EPN-47 company-creation flow adds another one — this
 * identity's seed value for `mockCompanyMembershipStore`, and what `resetMockCompanyMembershipsForTests()`
 * restores it back to (see that function's own doc comment — never called by product runtime code). */
const INITIAL_ADMIN_MEMBERSHIPS = [
  { membershipId: MOCK_MEMBERSHIP_ID, companyId: MOCK_COMPANY_ID, companyName: 'EPN Platform (mock)', roleId: COMPANY_ADMIN_ROLE_ID, roleName: 'CompanyAdmin' },
];

/** mfa@epn.local's own seed membership — a SEPARATE store entry (own membershipId, keyed by its own
 * MFA_USER_ID) from admin@epn.local's, even though both start out as the sole CompanyAdmin of the same
 * "EPN Platform (mock)" company (plausible in reality: two different admin users on one company). Kept
 * distinct so a company created while logged in as one identity is never accidentally visible to, or
 * confused with, the other's membership list. */
/** approver@epn.local's own seed membership — a SEPARATE store entry (own membershipId, keyed by its own
 * APPROVER_USER_ID) on the same "EPN Platform (mock)" company, mirroring how mfa@epn.local is modeled. */
const INITIAL_APPROVER_MEMBERSHIPS = [
  { membershipId: APPROVER_MEMBERSHIP_ID, companyId: MOCK_COMPANY_ID, companyName: 'EPN Platform (mock)', roleId: COMPANY_ADMIN_ROLE_ID, roleName: 'CompanyAdmin' },
];

const INITIAL_MFA_MEMBERSHIPS = [
  { membershipId: MFA_MEMBERSHIP_ID, companyId: MOCK_COMPANY_ID, companyName: 'EPN Platform (mock)', roleId: COMPANY_ADMIN_ROLE_ID, roleName: 'CompanyAdmin' },
];

/** Payroll History Import permission-matrix fixtures: one mock user per non-admin role, each the sole member of
 * the same "EPN Platform (mock)" company, so role-based UI gating (read-only, create-without-commit, no access)
 * is reachable without a backend. The Mock identity cannot grant real permissions — MockPayrollHistoryImport-
 * Gateway derives capabilities from the membership's role NAME, exactly as the server derives them from the
 * role's seeded permissions. All use the shared mock password. */
interface MockRoleUser {
  email: string;
  userId: string;
  membershipId: string;
  roleId: string;
  roleName: string;
  displayName: string;
}
const MOCK_ROLE_USERS: Record<string, MockRoleUser> = {
  'accountant@epn.local': { email: 'accountant@epn.local', userId: '88888888-8888-8888-8888-888888888801', membershipId: '88888888-8888-8888-8888-888888888811', roleId: 'role-accountant', roleName: 'Accountant', displayName: 'EPN Accountant' },
  'hr@epn.local': { email: 'hr@epn.local', userId: '88888888-8888-8888-8888-888888888802', membershipId: '88888888-8888-8888-8888-888888888812', roleId: 'role-hr-payroll-admin', roleName: 'HRPayrollAdmin', displayName: 'EPN HR Payroll Admin' },
  'employee@epn.local': { email: 'employee@epn.local', userId: '88888888-8888-8888-8888-888888888803', membershipId: '88888888-8888-8888-8888-888888888813', roleId: 'role-employee', roleName: 'Employee', displayName: 'EPN Employee' },
};
/** EPN-240: the mock identity's display-only permission list for the one role that is deliberately narrower (the real list comes from `GET /api/me`).
 * Roles not listed get none, which the UI reads as "unknown, show everything". */
const MOCK_ROLE_PERMISSIONS: Record<string, string[]> = {
  Accountant: ['custompayruns.read', 'payrollruns.read', 'employees.read'],
};
const roleUserMemberships = (u: MockRoleUser) => [
  { membershipId: u.membershipId, companyId: MOCK_COMPANY_ID, companyName: 'EPN Platform (mock)', roleId: u.roleId, roleName: u.roleName },
];

/** In-memory, no network calls — lets the UI be developed and demoed without a running API. */
export class MockIdentityGateway implements IdentityGateway {
  // Mirrors real cookie-session behavior: nobody is "logged in" until login() succeeds, so the app
  // shows the login page first on a fresh load, exactly like it would against the real API.
  private isLoggedIn = false;
  // Which mock identity getMe()/switchCompany() should answer as — the real backend resolves this from
  // the validated access token; this mock has no token to decode, so it tracks the same thing directly.
  private loggedInAs: 'admin' | 'approver' | 'mfa' | 'multi' | 'role' | 'partner' | null = null;
  private roleUser: MockRoleUser | null = null;
  private activeCompanyId: string | null = null;
  private members: MembershipResponse[] = [
    { id: 'm1', userId: MOCK_USER_ID, email: 'admin@epn.local', roleId: COMPANY_ADMIN_ROLE_ID, roleName: 'CompanyAdmin', isActive: true },
  ];
  private auditTrail: AuditEventPage['items'] = [
    {
      id: 'a1',
      occurredAtUtc: new Date().toISOString(),
      actorUserId: MOCK_USER_ID,
      actorEmailSnapshot: 'admin@epn.local',
      actorType: 'User',
      companyId: MOCK_COMPANY_ID,
      eventType: 'UserAuthenticated',
      targetType: 'User',
      targetId: MOCK_USER_ID,
      metadataJson: null,
      correlationId: 'c1',
    },
  ];

  // Mirrors the real backend's own single-live-challenge-per-account design: only ever one pending MFA
  // challenge at a time in this mock, keyed by the fixed MFA_CHALLENGE_TOKEN above.
  private pendingChallengeMethod: string | null = null;
  private pendingChallengeAttempts = 0;

  // EPN-49 — state lives in mockSecuritySettingsStore.ts (module-level, not a private field) so a test
  // file can reset it between tests via resetMockSecuritySettingsForTests(), the same reasoning as
  // mockCompanyMembershipStore.ts for memberships. No real TOTP math in this mock (there is no backend to
  // verify against); any begin/confirm pair accepts the fixed MFA_TEST_CODE this file already uses for the
  // login-time challenge mock.

  constructor() {
    // Seeds every mock identity's baseline membership list into the shared store this class and
    // MockCompaniesGateway both read/write — see mockCompanyMembershipStore.ts's own doc comment. One
    // membership source of truth per mock user id: admin@epn.local, mfa@epn.local, and multi@epn.local all
    // read/write through this same store in getMe()/switchCompany() below, so a company created for any of
    // the three is visible and switchable for that same identity, exactly like the real backend. A no-op
    // past the very first construction for a given user id (seedMockMemberships() only sets one that isn't
    // already present).
    seedMockMemberships(MOCK_USER_ID, INITIAL_ADMIN_MEMBERSHIPS);
    seedMockMemberships(MFA_USER_ID, INITIAL_MFA_MEMBERSHIPS);
    seedMockMemberships(MULTI_USER_ID, MULTI_MEMBERSHIPS);
    for (const u of Object.values(MOCK_ROLE_USERS)) seedMockMemberships(u.userId, roleUserMemberships(u));
    seedMockMemberships(APPROVER_USER_ID, INITIAL_APPROVER_MEMBERSHIPS);
  }

  async login(email: string, password: string, _rememberMe = false): Promise<LoginOutcome> {
    if (email === LOCKED_USER_EMAIL && password === 'password') {
      return { kind: 'locked', lockoutEndUtc: LOCKED_LOCKOUT_END_UTC };
    }
    if (email === MFA_USER_EMAIL && password === 'password') {
      // Mirrors the real backend's own preference (see AuthEndpoints' /login handler on the backend):
      // Authenticator is the default challenge method whenever it's enrolled, since it needs no delivery
      // step — the challenge is created already set to it, with no code send required to use it as-is.
      this.pendingChallengeMethod = 'Authenticator';
      this.pendingChallengeAttempts = 0;
      return { kind: 'mfaRequired', challengeToken: MFA_CHALLENGE_TOKEN, availableMethods: ['Email', 'Authenticator'] };
    }
    if (email === MULTI_USER_EMAIL && password === 'password') {
      this.isLoggedIn = true;
      this.loggedInAs = 'multi';
      setMockCurrentUser({ userId: MULTI_USER_ID, email: MULTI_USER_EMAIL });
      return { kind: 'success', userId: MULTI_USER_ID, email: MULTI_USER_EMAIL, memberships: getMockMemberships(MULTI_USER_ID) };
    }
    if (email === PARTNER_USER_EMAIL && password === 'password') {
      this.isLoggedIn = true;
      this.loggedInAs = 'partner';
      setMockCurrentUser({ userId: PARTNER_USER_ID, email: PARTNER_USER_EMAIL });
      return { kind: 'success', userId: PARTNER_USER_ID, email: PARTNER_USER_EMAIL, memberships: [] };
    }
    const roleUser = MOCK_ROLE_USERS[email];
    if (roleUser && password === 'password') {
      this.isLoggedIn = true;
      this.loggedInAs = 'role';
      this.roleUser = roleUser;
      return { kind: 'success', userId: roleUser.userId, email, memberships: getMockMemberships(roleUser.userId) };
    }
    if (email === APPROVER_USER_EMAIL && password === 'password') {
      this.isLoggedIn = true;
      this.loggedInAs = 'approver';
      setMockCurrentUser({ userId: APPROVER_USER_ID, email: APPROVER_USER_EMAIL });
      return { kind: 'success', userId: APPROVER_USER_ID, email: APPROVER_USER_EMAIL, memberships: getMockMemberships(APPROVER_USER_ID) };
    }
    if (email !== 'admin@epn.local' || password !== 'password') {
      throw new ApiError(401, 'Invalid email or password.');
    }
    this.isLoggedIn = true;
    this.loggedInAs = 'admin';
    setMockCurrentUser({ userId: MOCK_USER_ID, email: 'admin@epn.local' });
    return {
      kind: 'success',
      userId: MOCK_USER_ID,
      email: 'admin@epn.local',
      // EPN-47 company-creation sync: reads from the shared store rather than a hardcoded literal, so a
      // company created (and its CompanyAdmin membership granted) earlier in this same mock session is
      // already present on the very next login — matching the real backend, where memberships persist.
      memberships: getMockMemberships(MOCK_USER_ID),
    };
  }

  async logout(): Promise<void> {
    // Session/authentication state only — mirrors the real backend, which never deletes a
    // CompanyMembership row on logout. A company created earlier in this mock runtime (and
    // MockCompaniesGateway's own `companies` list, which this class has no reset hook over at all) must
    // still be there on the next login, exactly like production. Do NOT reset the shared membership store
    // here — see resetMockCompanyMembershipsForTests() below for the deliberate, test-only alternative.
    this.isLoggedIn = false;
    this.loggedInAs = null;
    this.roleUser = null;
    this.activeCompanyId = null;
    clearMockCurrentUser();
  }

  async mfaChallenge(challengeToken: string, method: string): Promise<void> {
    if (challengeToken !== MFA_CHALLENGE_TOKEN) {
      throw new ApiError(400, 'This verification session is invalid or has expired.');
    }
    if (method === 'Authenticator') {
      throw new ApiError(400, 'Authenticator does not use a delivery code — enter the code from your authenticator app.');
    }
    if (method !== 'Email') {
      throw new ApiError(400, 'This verification method is not available.');
    }
    this.pendingChallengeMethod = 'Email';
    // Never logged as a "sent" value the user could read off the console in place of their inbox — same
    // constraint as never rendering a real code in this codebase. This mock only ever accepts the one
    // fixed MFA_TEST_CODE below, documented here and in this file's own top-of-class doc comment.
  }

  async mfaVerify(challengeToken: string, method: string, code: string, rememberDevice: boolean): Promise<LoginResponse> {
    void rememberDevice; // No trusted-device persistence modeled in this mock — every login re-challenges.
    const invalid = () => new ApiError(400, 'This verification code is invalid or has expired.');

    if (challengeToken !== MFA_CHALLENGE_TOKEN || this.pendingChallengeMethod !== method) {
      throw invalid();
    }
    this.pendingChallengeAttempts += 1;
    if (this.pendingChallengeAttempts > 5) {
      throw invalid();
    }
    // Both enrolled mock methods (Email and Authenticator) accept the same fixed test code — this mock
    // has no real TOTP secret to compute against, so there is nothing to distinguish between them here.
    if (code !== MFA_TEST_CODE) {
      throw invalid();
    }

    this.pendingChallengeMethod = null;
    this.isLoggedIn = true;
    this.loggedInAs = 'mfa';
    setMockCurrentUser({ userId: MFA_USER_ID, email: MFA_USER_EMAIL });
    return {
      userId: MFA_USER_ID,
      email: MFA_USER_EMAIL,
      // Reads mfa@epn.local's own store entry — see this class's constructor and getMe()'s own comment.
      memberships: getMockMemberships(MFA_USER_ID),
    };
  }

  async forgotPassword(_email: string): Promise<void> {
    // Always resolves — mirrors the backend's enumeration-safe contract.
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    if (token !== VALID_RESET_TOKEN) {
      throw new ApiError(400, 'This link is invalid or has expired.');
    }
    const meetsComplexity =
      newPassword.length >= 8 &&
      /[A-Z]/.test(newPassword) &&
      /[a-z]/.test(newPassword) &&
      /[0-9]/.test(newPassword) &&
      /[^A-Za-z0-9]/.test(newPassword);
    if (!meetsComplexity) {
      throw new ApiError(
        400,
        'The new password must be at least 8 characters and include an uppercase letter, a lowercase letter, a digit, and a special character.',
      );
    }
  }

  async validateResetToken(token: string): Promise<boolean> {
    if (token === EXPIRED_RESET_TOKEN) {
      return false;
    }
    return token === VALID_RESET_TOKEN;
  }

  async requestUnlock(_email: string): Promise<void> {
    // Always resolves — mirrors the backend's enumeration-safe contract.
  }

  async completeUnlock(token: string): Promise<void> {
    if (token !== VALID_UNLOCK_TOKEN) {
      throw new ApiError(400, 'This link is invalid or has expired.');
    }
  }

  async changePassword(currentPassword: string): Promise<void> {
    if (!this.isLoggedIn) {
      throw new ApiError(401, 'Not authenticated.');
    }
    if (currentPassword !== 'password') {
      throw new ApiError(400, 'The current password is incorrect.');
    }
    this.isLoggedIn = false;
    this.loggedInAs = null;
    this.activeCompanyId = null;
    clearMockCurrentUser();
  }

  /** Maps the current mock session to the shared store's key for it — the one place that mapping lives,
   * so getMe() and switchCompany() can never disagree about which identity's membership list is "current"
   * (a prior version of this file read admin@epn.local's key for the mfa@epn.local session by mistake). */
  private currentStoreUserId(): string {
    if (this.loggedInAs === 'multi') return MULTI_USER_ID;
    if (this.loggedInAs === 'mfa') return MFA_USER_ID;
    if (this.loggedInAs === 'role' && this.roleUser) return this.roleUser.userId;
    if (this.loggedInAs === 'approver') return APPROVER_USER_ID;
    if (this.loggedInAs === 'partner') return PARTNER_USER_ID;
    return MOCK_USER_ID;
  }

  private readonly companyTimeZones = new Map<string, string>();

  async getMe(): Promise<MeResponse> {
    const me = await this.getMeWithoutTimeZone();
    const zone = me.activeCompanyId ? this.companyTimeZones.get(me.activeCompanyId) : undefined;
    return zone ? { ...me, activeCompanyTimeZoneId: zone } : me;
  }

  async setCompanyTimeZone(timeZoneId: string): Promise<CompanyTimeZoneResponse> {
    if (!this.isLoggedIn || !this.activeCompanyId) {
      throw new ApiError(401, 'Not authenticated.');
    }
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: timeZoneId });
    } catch {
      throw new ApiError(400, 'Choose a valid time zone, for example America/Los_Angeles.');
    }
    this.companyTimeZones.set(this.activeCompanyId, timeZoneId);
    return { timeZoneId };
  }

  private async getMeWithoutTimeZone(): Promise<MeResponse> {
    if (!this.isLoggedIn) {
      throw new ApiError(401, 'Not authenticated.');
    }
    const memberships = getMockMemberships(this.currentStoreUserId());
    // Looked up from the current membership list rather than assumed from a fixed role-id constant — a
    // company created after this session started may carry a different roleId than the identity's
    // original membership(s) did.
    const activeMembership = this.activeCompanyId ? memberships.find((m) => m.companyId === this.activeCompanyId) : undefined;
    if (this.loggedInAs === 'multi') {
      return {
        userId: MULTI_USER_ID,
        email: MULTI_USER_EMAIL,
        displayName: 'Multi-Company Admin',
        activeCompanyId: this.activeCompanyId,
        activeRoleId: activeMembership?.roleId ?? null,
        memberships,
      };
    }
    if (this.loggedInAs === 'role' && this.roleUser) {
      return {
        userId: this.roleUser.userId,
        email: this.roleUser.email,
        displayName: this.roleUser.displayName,
        activeCompanyId: this.activeCompanyId,
        activeRoleId: activeMembership?.roleId ?? null,
        memberships,
        ...(MOCK_ROLE_PERMISSIONS[this.roleUser.roleName] ? { activePermissions: MOCK_ROLE_PERMISSIONS[this.roleUser.roleName] } : {}),
      };
    }
    if (this.loggedInAs === 'partner') {
      const delegation = getMockDelegation();
      return {
        userId: PARTNER_USER_ID, email: PARTNER_USER_EMAIL, displayName: 'Pat Partner', activeCompanyId: null, activeRoleId: null, memberships: [],
        ...(delegation ? { partnerDelegation: delegation.info, activePermissions: delegation.permissions } : {}),
      };
    }
    if (this.loggedInAs === 'approver') {
      return {
        userId: APPROVER_USER_ID,
        email: APPROVER_USER_EMAIL,
        displayName: 'EPN Approver',
        activeCompanyId: this.activeCompanyId,
        activeRoleId: activeMembership?.roleId ?? null,
        memberships,
      };
    }
    // 'admin' and 'mfa' share the exact same displayName/response shape (unchanged from before EPN-47) —
    // only the signed-in email/userId/store-key differ, and nothing currently reads those two apart in a
    // way that matters to any existing test.
    const isMfa = this.loggedInAs === 'mfa';
    return {
      userId: isMfa ? MFA_USER_ID : MOCK_USER_ID,
      email: isMfa ? MFA_USER_EMAIL : 'admin@epn.local',
      displayName: 'EPN Administrator',
      activeCompanyId: this.activeCompanyId,
      activeRoleId: activeMembership?.roleId ?? null,
      memberships,
    };
  }

  async switchCompany(membershipId: string): Promise<{ roleId: string }> {
    if (!this.isLoggedIn) {
      throw new ApiError(401, 'Not authenticated.');
    }
    // Validated against the same shared, mutable per-identity list getMe() reads — accepts both an
    // identity's original seed membership(s) and any membership a company-creation flow has since added
    // for that same user id.
    const membership = getMockMemberships(this.currentStoreUserId()).find((m) => m.membershipId === membershipId);
    if (!membership) {
      throw new ApiError(403, 'The requested membership does not exist, is not yours, or is no longer active.');
    }
    this.activeCompanyId = membership.companyId;
    return { roleId: membership.roleId };
  }

  async listRoles(): Promise<RoleResponse[]> {
    return [...MOCK_ROLES];
  }

  async listGrantsForRole(roleId: string): Promise<PermissionResponse[]> {
    if (roleId === SUPERADMIN_ROLE_ID) {
      return [
        { id: 'p1', code: 'companies.read', description: 'View companies.' },
        { id: 'p2', code: 'companies.write', description: 'Create or edit companies.' },
        { id: 'p3', code: 'employees.read', description: 'View employee records.' },
        { id: 'p4', code: 'memberships.write', description: 'Add, revoke or change the role of a company member.' },
      ];
    }
    return [];
  }

  async listMembers(): Promise<MembershipResponse[]> {
    return this.members;
  }

  async revokeMember(_companyId: string, membershipId: string): Promise<void> {
    this.members = this.members.filter((m) => m.id !== membershipId);
  }

  async changeMembershipRole(_companyId: string, membershipId: string, roleId: string): Promise<MembershipResponse> {
    const member = this.members.find((m) => m.id === membershipId);
    if (!member) {
      throw new ApiError(404, 'Membership not found.');
    }
    const role = MOCK_ROLES.find((r) => r.id === roleId);
    if (!role) {
      throw new ApiError(400, 'Unknown role.');
    }
    // Mirrors MembershipService.ChangeRoleAsync: SuperAdmin is an internal/platform role, never assignable
    // through an ordinary company membership action.
    if (role.id === SUPERADMIN_ROLE_ID) {
      throw new ApiError(400, 'SuperAdmin cannot be assigned to a company membership.');
    }
    const roleName = role.name;
    const updated: MembershipResponse = { ...member, roleId, roleName };
    this.members = this.members.map((m) => (m.id === membershipId ? updated : m));
    return updated;
  }

  async getAuditTrail(page: number, pageSize: number): Promise<AuditEventPage> {
    return { items: this.auditTrail, page, pageSize, totalCount: this.auditTrail.length };
  }

  /** The mock behaves like a deployment that delivers e-mail, so existing flows keep their normal wording. */
  async getEmailDelivery(): Promise<{ enabled: boolean }> {
    return { enabled: true };
  }

  async listMfaEnrollments(): Promise<MfaEnrollmentResponse[]> {
    return getMockMfaEnrollments();
  }

  /** Mirrors the API: changing MFA or trusted devices needs a recent step-up verification (the fixed test code, like every
   * other mock code), otherwise 403 `STEP_UP_REQUIRED` and nothing changes. */
  private requireStepUp(): void {
    const { verifiedAtMs } = getMockStepUp();
    if (verifiedAtMs === null || Date.now() - verifiedAtMs > MOCK_STEP_UP_WINDOW_MS) {
      throw new ApiError(
        403, 'Please verify with the code we email you before making this change.', null, true, 'STEP_UP_REQUIRED',
      );
    }
  }

  async requestStepUp(): Promise<void> {
    setMockStepUp({ requested: true, verifiedAtMs: getMockStepUp().verifiedAtMs });
  }

  async verifyStepUp(code: string): Promise<void> {
    if (!getMockStepUp().requested || code !== MFA_TEST_CODE) {
      throw new ApiError(400, 'This verification code is invalid or has expired.');
    }
    setMockStepUp({ requested: false, verifiedAtMs: Date.now() });
  }

  async beginAuthenticatorEnrollment(): Promise<BeginAuthenticatorEnrollmentResponse> {
    this.requireStepUp();
    const secret = 'MOCKSECRETFORTESTONLYUSEAPP';
    setPendingAuthenticatorSecret(secret);
    return { secret, otpAuthUri: `otpauth://totp/Escochex%20Payroll:mock?secret=${secret}&issuer=Escochex%20Payroll&digits=6&period=30` };
  }

  async confirmAuthenticatorEnrollment(secret: string, code: string): Promise<MfaEnrollmentResponse> {
    this.requireStepUp();
    if (secret !== getPendingAuthenticatorSecret() || code !== MFA_TEST_CODE) {
      throw new ApiError(400, 'This verification code is invalid or has expired.');
    }
    setPendingAuthenticatorSecret(null);
    const enrollment: MfaEnrollmentResponse = {
      method: 'Authenticator', isEnabled: true, enrolledAtUtc: new Date().toISOString(), disabledAtUtc: null,
    };
    setMockMfaEnrollments([enrollment]);
    return enrollment;
  }

  async disableAuthenticatorEnrollment(): Promise<void> {
    this.requireStepUp();
    const current = getMockMfaEnrollments();
    const active = current.find((e) => e.method === 'Authenticator' && e.isEnabled);
    if (!active) {
      throw new ApiError(404, 'No active Authenticator enrollment to disable.');
    }
    // A new array with a new object (never mutate the existing one in place) — React's state-change
    // detection relies on a new reference reaching setEnrollments() on the next load(), the same reason
    // every other mock mutation in this file replaces its array rather than editing an element in place.
    setMockMfaEnrollments(current.map((e) =>
      e === active ? { ...e, isEnabled: false, disabledAtUtc: new Date().toISOString() } : e,
    ));
  }

  async listTrustedDevices(): Promise<TrustedDeviceResponse[]> {
    return getMockTrustedDevices();
  }

  async revokeTrustedDevice(deviceId: string): Promise<void> {
    this.requireStepUp();
    const exists = getMockTrustedDevices().some((d) => d.id === deviceId);
    if (!exists) {
      throw new ApiError(404, 'No such trusted device.');
    }
    setMockTrustedDevices(getMockTrustedDevices().filter((d) => d.id !== deviceId));
  }
}

/** TEST-ONLY. Never called by logout() or any other product runtime path (see this file's own comment on
 * logout() for why — resetting on logout would silently reintroduce the original company-creation sync
 * bug after a relogin). Restores all four mock identities' (admin@epn.local, mfa@epn.local,
 * multi@epn.local, approver@epn.local) shared-store membership lists back to their original seed values, for deliberate use in
 * a test file's own `beforeEach`/`afterEach` where a test needs a pristine starting point rather than
 * whatever an earlier `it` block in the same file left behind. */
export function resetMockCompanyMembershipsForTests(): void {
  resetMockMemberships(MOCK_USER_ID, INITIAL_ADMIN_MEMBERSHIPS);
  resetMockMemberships(MFA_USER_ID, INITIAL_MFA_MEMBERSHIPS);
  resetMockMemberships(MULTI_USER_ID, MULTI_MEMBERSHIPS);
  resetMockMemberships(APPROVER_USER_ID, INITIAL_APPROVER_MEMBERSHIPS);
}

/** TEST-ONLY — re-exported here so a test file only needs one import from this module. See
 * mockSecuritySettingsStore.ts's own doc comment. */
export { resetMockSecuritySettingsForTests } from './mockSecuritySettingsStore';
